import { Injectable } from '@angular/core';
import { EnvService } from '../helpers/env.service';

export const PKCE_VERIFIER_KEY = 'pkce_verifier';
export const PKCE_STATE_KEY = 'pkce_state';
/** Page to open after the login instead of home (an app URL such as `/vi/hpc/register-org/…`). */
export const PKCE_RETURN_URL_KEY = 'pkce_return_url';

@Injectable({ providedIn: 'root' })
export class KeycloakPkceService {

    constructor(private env: EnvService) {}

    /** `returnUrl`: the app page to come back to once logged in; home when omitted. */
    async startLogin(returnUrl?: string): Promise<void> {
        const verifier = this.randomBase64url(64);
        const challenge = await this.sha256Base64url(verifier);
        const state = this.randomBase64url(16);

        sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
        sessionStorage.setItem(PKCE_STATE_KEY, state);
        if (returnUrl) {
            sessionStorage.setItem(PKCE_RETURN_URL_KEY, returnUrl);
        } else {
            sessionStorage.removeItem(PKCE_RETURN_URL_KEY);
        }

        const authUrl = `${this.env.KEYCLOAK_URL}/realms/${this.env.KEYCLOAK_REALM}/protocol/openid-connect/auth`;

        const params = new URLSearchParams({
            client_id: this.env.KEYCLOAK_CLIENT_ID,
            response_type: 'code',
            scope: 'openid',
            code_challenge: challenge,
            code_challenge_method: 'S256',
            redirect_uri: window.location.origin,
            state,
        });

        window.location.href = authUrl + '?' + params.toString();
    }

    extractCallback(): { code: string; codeVerifier: string } | null {
        const params = new URLSearchParams(window.location.search);
        const code = params.get('code');
        const state = params.get('state');

        if (!code) return null;

        const savedState = sessionStorage.getItem(PKCE_STATE_KEY);
        if (state && savedState && state !== savedState) {
            console.error('[PKCE] state mismatch — possible CSRF');
            return null;
        }

        const codeVerifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);
        if (!codeVerifier) {
            console.error('[PKCE] code_verifier not found in sessionStorage');
            return null;
        }

        window.history.replaceState({}, '', window.location.pathname);
        sessionStorage.removeItem(PKCE_VERIFIER_KEY);
        sessionStorage.removeItem(PKCE_STATE_KEY);

        return { code, codeVerifier };
    }

    /** The page startLogin was asked to come back to, read once; only app paths are trusted. */
    takeReturnUrl(): string | null {
        const returnUrl = sessionStorage.getItem(PKCE_RETURN_URL_KEY);
        sessionStorage.removeItem(PKCE_RETURN_URL_KEY);
        return returnUrl?.startsWith('/') && !returnUrl.startsWith('//') ? returnUrl : null;
    }

    private randomBase64url(byteLength: number): string {
        const arr = new Uint8Array(byteLength);
        crypto.getRandomValues(arr);
        return btoa(String.fromCharCode(...arr))
            .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
    }

    private async sha256Base64url(plain: string): Promise<string> {
        const enc = new TextEncoder().encode(plain);
        const hash = await crypto.subtle.digest('SHA-256', enc);
        return btoa(String.fromCharCode(...new Uint8Array(hash)))
            .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
    }
}
